import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

// ─── Constants ────────────────────────────────────────────────────────────────
const SCOPE_LABELS = { project: '📁 Project Roles', global: '🌐 Global Roles' };

const PERMISSION_GROUPS = {
  project: [
    { group: 'Test Cases',    perms: ['testcase:create','testcase:read','testcase:update','testcase:delete'] },
    { group: 'Test Suites',   perms: ['testsuite:create','testsuite:read','testsuite:update','testsuite:delete'] },
    { group: 'Requirements',  perms: ['requirement:create','requirement:read','requirement:update','requirement:delete'] },
    { group: 'Test Runs',     perms: ['testrun:create','testrun:read','testrun:update','testrun:delete','testrun:push_results'] },
    { group: 'Folders',       perms: ['folder:create','folder:read','folder:update','folder:delete'] },
    { group: 'Project',       perms: ['project:read','project:update','project:delete'] },
    { group: 'Members & Keys', perms: ['member:manage','apikey:generate'] },
  ],
  global: [
    { group: 'Projects',      perms: ['project:create','project:edit','project:archive'] },
    { group: 'Settings',      perms: ['settings:read','settings:write'] },
    { group: 'Users',         perms: ['users:manage','users:create','users:role_change'] },
    { group: 'Integrations',  perms: ['jira:config','jenkins:config'] },
    { group: 'Teams',         perms: ['teams:manage'] },
  ],
};

const PERM_LABEL = p => {
  const action = p.split(':')[1] || p;
  return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const ROLE_COLORS = [
  { bg: '#fef3c7', color: '#92400e' },
  { bg: '#ede9fe', color: '#5b21b6' },
  { bg: '#fce7f3', color: '#9d174d' },
  { bg: '#dbeafe', color: '#1e40af' },
  { bg: '#d1fae5', color: '#065f46' },
  { bg: '#e0e7ff', color: '#3730a3' },
  { bg: '#fef9c3', color: '#713f12' },
  { bg: '#ffe4e6', color: '#9f1239' },
];
const roleColor = idx => ROLE_COLORS[idx % ROLE_COLORS.length];

// ─── Confirm dialog ───────────────────────────────────────────────────────────
function useConfirm() {
  const [state, setState] = useState({ open: false, msg: '', resolve: null });
  const confirm = msg => new Promise(res => setState({ open: true, msg, resolve: res }));
  const Dialog = state.open ? (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered modal-sm">
        <div className="modal-content">
          <div className="modal-body py-4 text-center" style={{ fontSize: 14 }}>{state.msg}</div>
          <div className="modal-footer border-0 pt-0 justify-content-center gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={() => { state.resolve(false); setState(s => ({ ...s, open: false })); }}>Cancel</button>
            <button className="btn btn-danger btn-sm" onClick={() => { state.resolve(true); setState(s => ({ ...s, open: false })); }}>Confirm</button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
  return { confirm, Dialog };
}

// ─── Permission Checkbox Grid ─────────────────────────────────────────────────
function PermissionGrid({ scope, selected, onChange }) {
  const groups = PERMISSION_GROUPS[scope] || [];
  const allInGroup = perms => perms.every(p => selected.has(p));
  const toggle = perm => {
    const next = new Set(selected);
    next.has(perm) ? next.delete(perm) : next.add(perm);
    onChange(next);
  };
  const toggleGroup = (perms, on) => {
    const next = new Set(selected);
    perms.forEach(p => on ? next.add(p) : next.delete(p));
    onChange(next);
  };
  return (
    <div className="row g-3">
      {groups.map(({ group, perms }) => {
        const all = allInGroup(perms);
        return (
          <div key={group} className="col-md-6">
            <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div className="card-body" style={{ padding: '10px 14px' }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>{group}</span>
                  <button type="button" className={`btn btn-sm ${all ? 'btn-primary' : 'btn-outline-secondary'}`} style={{ fontSize: 11, padding: '1px 8px' }} onClick={() => toggleGroup(perms, !all)}>
                    {all ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {perms.map(p => (
                    <button key={p} type="button" onClick={() => toggle(p)}
                      className={`btn btn-sm ${selected.has(p) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20 }}>
                      {PERM_LABEL(p)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Edit Role Modal ──────────────────────────────────────────────────────────
function EditRoleModal({ role, allPermissions, scope, onSave, onClose }) {
  const [label, setLabel] = useState(role?.label || '');
  const [description, setDescription] = useState(role?.description || '');
  const [selected, setSelected] = useState(new Set(role?.permissions || []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!label.trim()) { setError('Label is required'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ label: label.trim(), description: description.trim(), permissions: [...selected] });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">✏️ Edit Role — <code>{role?.role}</code></h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
            <div className="row g-3 mb-4">
              <div className="col-md-5">
                <label className="form-label fw-semibold">Display Label <span className="text-danger">*</span></label>
                <input className="form-control" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Senior Tester" />
              </div>
              <div className="col-md-7">
                <label className="form-label fw-semibold">Description</label>
                <input className="form-control" value={description} onChange={e => setDescription(e.target.value)} placeholder="What this role is for…" />
              </div>
            </div>
            <p className="fw-semibold mb-3" style={{ fontSize: 13 }}>
              Permissions <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({selected.size} of {allPermissions.length} selected)</span>
            </p>
            <PermissionGrid scope={scope} selected={selected} onChange={setSelected} />
          </div>
          <div className="modal-footer">
            <span className="text-muted small me-auto">
              {role?.isSystem && !role?.isCustom ? '⚠️ Built-in role — changes will override the defaults.' : ''}
            </span>
            <button className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : '💾 Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── New Role Modal ───────────────────────────────────────────────────────────
function NewRoleModal({ allPermissions, scope, onSave, onClose }) {
  const [roleKey, setRoleKey] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!roleKey.trim()) { setError('Role key is required'); return; }
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(roleKey)) { setError('Role key: lowercase letters, numbers and underscores only (e.g. senior_tester)'); return; }
    if (!label.trim()) { setError('Display label is required'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ scope, role: roleKey.trim(), label: label.trim(), description: description.trim(), permissions: [...selected] });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create role');
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">➕ New Custom Role</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Role Key <span className="text-danger">*</span></label>
                <input className="form-control font-monospace" value={roleKey} onChange={e => setRoleKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="e.g. senior_tester" autoFocus />
                <small className="text-muted">Lowercase + underscores only. Cannot be changed later.</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Display Label <span className="text-danger">*</span></label>
                <input className="form-control" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Senior Tester" />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Description</label>
                <input className="form-control" value={description} onChange={e => setDescription(e.target.value)} placeholder="What this role is for…" />
              </div>
            </div>
            <p className="fw-semibold mb-3" style={{ fontSize: 13 }}>
              Permissions <span className="text-muted fw-normal" style={{ fontSize: 12 }}>({selected.size} of {allPermissions.length} selected)</span>
            </p>
            <PermissionGrid scope={scope} selected={selected} onChange={setSelected} />
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2" />Creating…</> : '➕ Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Role Card ────────────────────────────────────────────────────────────────
function RoleCard({ role, index, isAdmin, onEdit, onDelete }) {
  const c = roleColor(index);
  const permCount = role.permissions?.length || 0;
  return (
    <div className="card h-100" style={{ borderTop: `3px solid ${c.color}` }}>
      <div className="card-body d-flex flex-column" style={{ padding: '14px 16px' }}>
        <div className="d-flex align-items-start justify-content-between mb-2">
          <span className="badge" style={{ background: c.bg, color: c.color, fontSize: 12, padding: '4px 10px', fontWeight: 600 }}>
            {role.label}
          </span>
          <div className="d-flex gap-1 flex-wrap justify-content-end">
            {role.isOverridden && (
              <span style={{ fontSize: 11, color: '#0369a1', background: '#e0f2fe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>Modified</span>
            )}
            {role.isCustom && (
              <span style={{ fontSize: 11, color: '#059669', background: '#d1fae5', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>Custom</span>
            )}
            {role.isSystem && !role.isOverridden && !role.isCustom && (
              <span style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>Built-in</span>
            )}
          </div>
        </div>
        <code style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>{role.role}</code>
        <p className="text-muted mb-0 flex-grow-1" style={{ fontSize: 12, lineHeight: 1.4 }}>{role.description || '—'}</p>
        <div className="d-flex align-items-center justify-content-between mt-3">
          <span style={{ fontSize: 11, color: '#64748b' }}>{permCount} permission{permCount !== 1 ? 's' : ''}</span>
          {isAdmin && (
            <div className="d-flex gap-1">
              <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 12, padding: '2px 10px' }} onClick={() => onEdit(role)}>✏️ Edit</button>
              {(role.isCustom || role.isOverridden) && (
                <button className="btn btn-outline-danger btn-sm" style={{ fontSize: 12, padding: '2px 8px' }} onClick={() => onDelete(role)} title={role.isCustom ? 'Delete role' : 'Reset to defaults'}>
                  {role.isCustom ? '🗑️' : '↩️'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function RolesPermissions() {
  const [scope, setScope] = useState('project');
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const { confirm, Dialog: ConfirmDialog } = useConfirm();

  const loadRoles = useCallback(async (s) => {
    setLoading(true); setError('');
    try {
      const res = await api.get(`/roles?scope=${s}`);
      setRoles(res.data.data || []);
      setAllPermissions(res.data.allPermissions || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      setIsAdmin(u.role === 'admin' || !!u.isSuperAdmin);
    } catch { setIsAdmin(false); }
    loadRoles(scope);
  }, [scope, loadRoles]);

  const flash = msg => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3500); };

  const handleSaveEdit = async (updates) => {
    await api.put(`/roles/${editingRole.role}`, { scope, ...updates });
    setEditingRole(null);
    flash('✅ Role saved.');
    loadRoles(scope);
  };

  const handleCreate = async (data) => {
    await api.post('/roles', data);
    setShowNewModal(false);
    flash('✅ Custom role created.');
    loadRoles(scope);
  };

  const handleDelete = async (role) => {
    const msg = role.isCustom
      ? `Delete the custom role "${role.label}"? This cannot be undone.`
      : `Reset "${role.label}" permissions back to system defaults?`;
    const ok = await confirm(msg);
    if (!ok) return;
    try {
      await api.delete(`/roles/${role.role}?scope=${scope}`);
      flash(role.isCustom ? '🗑️ Role deleted.' : '↩️ Role reset to defaults.');
      loadRoles(scope);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/settings">Settings</Link></li>
              <li className="breadcrumb-item active">Roles &amp; Permissions</li>
            </ol>
          </nav>
          <h1 className="page-title">🔑 Roles &amp; Permissions</h1>
          <p className="page-subtitle">
            Manage what each role can do — create custom roles or override built-in permissions.
          </p>
        </div>
        <div className="d-flex gap-2">
          <Link to="/settings/users" className="btn btn-outline-secondary">👤 Users</Link>
          {isAdmin && (
            <button className="btn btn-primary fw-semibold" onClick={() => setShowNewModal(true)}>
              ➕ New Role
            </button>
          )}
        </div>
      </div>

      <div className="container-fluid">
        {successMsg && <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>{successMsg}</div>}
        {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}

        {/* Scope tabs */}
        <ul className="nav nav-tabs mb-4">
          {Object.entries(SCOPE_LABELS).map(([s, label]) => (
            <li key={s} className="nav-item">
              <button className={`nav-link ${scope === s ? 'active' : ''}`} onClick={() => setScope(s)}>{label}</button>
            </li>
          ))}
        </ul>

        {!isAdmin && (
          <div className="alert alert-info d-flex align-items-center gap-2 mb-4" style={{ fontSize: 13 }}>
            <span>ℹ️</span>
            <span>Read-only view. Only <strong>admins</strong> can add or modify roles.</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" role="status" /></div>
        ) : (
          <>
            {/* Role cards */}
            <div className="row g-3 mb-5">
              {roles.map((role, i) => (
                <div key={role.role} className="col-md-4 col-lg-3">
                  <RoleCard role={role} index={i} isAdmin={isAdmin} onEdit={setEditingRole} onDelete={handleDelete} />
                </div>
              ))}
              {roles.length === 0 && (
                <div className="col-12">
                  <div className="empty-state">
                    <div style={{ fontSize: 40 }}>🔑</div>
                    <p>No roles configured yet.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Permissions matrix */}
            <div className="card mb-4">
              <div className="card-header d-flex align-items-center justify-content-between">
                <strong>Permissions Matrix</strong>
                <span className="text-muted small">
                  {scope === 'project' ? 'What each project role can do within a project' : 'What each global role can do across the workspace'}
                </span>
              </div>
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0" style={{ fontSize: 12 }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 130 }}>Group</th>
                      <th style={{ width: 200 }}>Permission</th>
                      {roles.map((r, i) => {
                        const c = roleColor(i);
                        return (
                          <th key={r.role} className="text-center" style={{ minWidth: 90 }}>
                            <span className="badge" style={{ background: c.bg, color: c.color, fontSize: 11, padding: '3px 8px' }}>{r.label}</span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {(PERMISSION_GROUPS[scope] || []).map(({ group, perms }) =>
                      perms.map((perm, pi) => (
                        <tr key={perm}>
                          {pi === 0 ? (
                            <td rowSpan={perms.length} style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', verticalAlign: 'middle', borderRight: '1px solid #f1f5f9' }}>
                              {group}
                            </td>
                          ) : null}
                          <td>
                            <code style={{ fontSize: 11 }}>{perm}</code>
                            <span className="text-muted ms-1" style={{ fontSize: 11 }}>({PERM_LABEL(perm)})</span>
                          </td>
                          {roles.map(r => (
                            <td key={r.role} className="text-center">
                              {r.permissions?.includes(perm)
                                ? <span style={{ color: '#16a34a', fontSize: 15 }}>✅</span>
                                : <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Info callout */}
            <div className="card" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div className="card-body" style={{ padding: '14px 18px' }}>
                <p className="fw-semibold mb-2" style={{ fontSize: 13, color: '#1e293b' }}>ℹ️ How roles work</p>
                <ul className="mb-0" style={{ fontSize: 13, color: '#475569', paddingLeft: 18, lineHeight: 1.8 }}>
                  <li><strong>Built-in roles</strong> ship with sensible defaults. Use ✏️ Edit to override their permissions — changes take effect immediately.</li>
                  <li>Use ↩️ to <strong>reset a built-in role</strong> back to its original permissions at any time.</li>
                  <li><strong>Custom roles</strong> (marked <span style={{ color: '#059669', fontWeight: 600 }}>Custom</span>) can be freely created and deleted.</li>
                  <li>
                    To assign a project role, go to <strong>Project Settings → Members &amp; Roles</strong>.
                    To change a global role, visit <Link to="/settings/users">User Management</Link>.
                  </li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      {editingRole && (
        <EditRoleModal role={editingRole} allPermissions={allPermissions} scope={scope} onSave={handleSaveEdit} onClose={() => setEditingRole(null)} />
      )}
      {showNewModal && (
        <NewRoleModal allPermissions={allPermissions} scope={scope} onSave={handleCreate} onClose={() => setShowNewModal(false)} />
      )}
      {ConfirmDialog}
    </div>
  );
}
